import mapboxgl, { type MapMouseEvent, type Marker } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { fetchDirections } from '@/features/mapbox';
import type { IMapboxCoordinates } from '@/types/mapbox';
import { MapLayersService } from '../utils/MapLayersService';

/**
 * Information about a computed driving route — the distance (meters) and
 * duration (seconds) of the first route returned by the Directions API.
 */
interface RouteInfo {
  distance: number;
  duration: number;
}

interface UseDrivingRouteOptions {
  /** Ref to the live Mapbox map. Click handling and markers attach to it. */
  map: React.RefObject<mapboxgl.Map | null>;
  /**
   * Layers service used to draw/remove the route line. When omitted, the hook
   * builds one from `map.current` on first use (matching how the modal/MapShell
   * construct `new MapLayersService(map.current)`).
   */
  layersService?: MapLayersService;
  /** Fired exactly once per successful computation. Analytics-agnostic. */
  onRouteComputed?: (info: RouteInfo) => void;
}

interface UseDrivingRouteResult {
  pointA: IMapboxCoordinates | null;
  pointB: IMapboxCoordinates | null;
  routeInfo: RouteInfo;
  isLoading: boolean;
  isListening: boolean;
  /** True when the last computation failed (Directions returned nothing) — drives "route unavailable". */
  routeError: boolean;
  arm: () => void;
  clearPointA: () => void;
  clearPointB: () => void;
  swap: () => void;
  reset: () => void;
}

const ROUTE_INFO_DEFAULT: RouteInfo = { distance: 0, duration: 0 };

/**
 * Project-agnostic driving-route state machine. Drives the two-point (A → B)
 * picking flow on a Mapbox map: arm listening, capture A then B from successive
 * map clicks, auto-compute the driving route via the Mapbox Directions API, and
 * expose clear/swap/reset.
 *
 * State invariants:
 * - `getNextPointToFill` returns 'A' until A is set, then 'B', then null — so
 *   once both points exist extra map clicks are ignored.
 * - Clearing a point removes its marker + the route layer, resets `routeInfo`,
 *   and re-arms listening for the now-missing point (via the effect below).
 * - On a successful fetch the route layer is drawn, `routeInfo` is set from
 *   `routes[0]`, and `onRouteComputed` fires once. On failure (`undefined`) the
 *   state is left usable — no throw, no route layer, `routeInfo` untouched.
 *
 * Never mutates inputs: every state transition produces new objects.
 */
export function useDrivingRoute({
  map,
  layersService,
  onRouteComputed,
}: UseDrivingRouteOptions): UseDrivingRouteResult {
  const [pointA, setPointA] = useState<IMapboxCoordinates | null>(null);
  const [pointB, setPointB] = useState<IMapboxCoordinates | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo>(ROUTE_INFO_DEFAULT);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [routeError, setRouteError] = useState(false);

  const clickHandlerRef = useRef<((e: MapMouseEvent) => void) | null>(null);
  const markerARef = useRef<Marker | null>(null);
  const markerBRef = useRef<Marker | null>(null);

  // Refs mirror state so the (stable) map click handler reads current values.
  const pointARef = useRef<IMapboxCoordinates | null>(null);
  const pointBRef = useRef<IMapboxCoordinates | null>(null);

  // Lazily-built fallback service so the hook works when none is supplied.
  const fallbackServiceRef = useRef<MapLayersService | null>(null);

  // True once the user has armed the flow; gates the auto re-arm-on-clear
  // effect so a full `reset()` (which disarms) does not immediately re-arm.
  const armedRef = useRef(false);

  const getLayersService = useCallback((): MapLayersService | null => {
    if (layersService) return layersService;
    if (fallbackServiceRef.current) return fallbackServiceRef.current;
    if (!map.current) return null;
    fallbackServiceRef.current = new MapLayersService(map.current);
    return fallbackServiceRef.current;
  }, [layersService, map]);

  useEffect(() => {
    pointARef.current = pointA;
  }, [pointA]);

  useEffect(() => {
    pointBRef.current = pointB;
  }, [pointB]);

  const getNextPointToFill = useCallback((): 'A' | 'B' | null => {
    if (!pointA) return 'A';
    if (!pointB) return 'B';
    return null;
  }, [pointA, pointB]);

  const removeMarkerA = useCallback(() => {
    if (markerARef.current) {
      markerARef.current.remove();
      markerARef.current = null;
    }
  }, []);

  const removeMarkerB = useCallback(() => {
    if (markerBRef.current) {
      markerBRef.current.remove();
      markerBRef.current = null;
    }
  }, []);

  const addMarkerA = useCallback(
    (coords: IMapboxCoordinates) => {
      if (!map.current) return;
      removeMarkerA();
      const el = document.createElement('div');
      const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
        .setLngLat([coords.lng, coords.lat])
        .addTo(map.current);
      marker.addClassName('routing-a-marker');
      markerARef.current = marker;
    },
    [map, removeMarkerA],
  );

  const addMarkerB = useCallback(
    (coords: IMapboxCoordinates) => {
      if (!map.current) return;
      removeMarkerB();
      const el = document.createElement('div');
      const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
        .setLngLat([coords.lng, coords.lat])
        .addTo(map.current);
      marker.addClassName('routing-b-marker');
      markerBRef.current = marker;
    },
    [map, removeMarkerB],
  );

  const stopListening = useCallback(() => {
    if (map.current && clickHandlerRef.current) {
      map.current.off('click', clickHandlerRef.current);
      clickHandlerRef.current = null;
    }
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
    }
    setIsListening(false);
  }, [map]);

  const arm = useCallback(() => {
    if (!map.current) {
      toast.error('Map is not ready');
      return;
    }

    armedRef.current = true;

    // Stop any existing listener first.
    stopListening();

    const nextPoint = getNextPointToFill();
    if (!nextPoint) {
      return; // Both points are filled.
    }

    map.current.getCanvas().style.cursor = 'crosshair';

    // Handler reads refs to access the latest state values.
    const handler = (e: MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      const coords = { lng, lat };

      if (!pointARef.current) {
        setPointA(coords);
        addMarkerA(coords);
        if (!pointBRef.current) {
          toast.info('Now click to select Point B');
        }
      } else if (!pointBRef.current) {
        setPointB(coords);
        addMarkerB(coords);
        stopListening();
      }
    };

    clickHandlerRef.current = handler;
    map.current.on('click', handler);
    setIsListening(true);

    const pointLabel = nextPoint === 'A' ? 'Point A' : 'Point B';
    toast.info(`Click on the map to select ${pointLabel}`);
  }, [map, getNextPointToFill, addMarkerA, addMarkerB, stopListening]);

  const clearPointA = useCallback(() => {
    setPointA(null);
    removeMarkerA();
    setRouteInfo(ROUTE_INFO_DEFAULT);
    setRouteError(false);
    getLayersService()?.removeRouting();
  }, [removeMarkerA, getLayersService]);

  const clearPointB = useCallback(() => {
    setPointB(null);
    removeMarkerB();
    setRouteInfo(ROUTE_INFO_DEFAULT);
    setRouteError(false);
    getLayersService()?.removeRouting();
  }, [removeMarkerB, getLayersService]);

  const swap = useCallback(() => {
    const tempA = pointA;
    const tempB = pointB;

    setPointA(tempB);
    setPointB(tempA);

    if (tempB) {
      addMarkerA(tempB);
    } else {
      removeMarkerA();
    }

    if (tempA) {
      addMarkerB(tempA);
    } else {
      removeMarkerB();
    }

    // No explicit recompute trigger needed: swapping changes pointA/pointB, so
    // the auto-compute effect below recomputes exactly once for the new pair.
  }, [pointA, pointB, addMarkerA, addMarkerB, removeMarkerA, removeMarkerB]);

  const reset = useCallback(() => {
    armedRef.current = false;
    stopListening();
    removeMarkerA();
    removeMarkerB();
    setPointA(null);
    setPointB(null);
    setRouteInfo(ROUTE_INFO_DEFAULT);
    setRouteError(false);
    getLayersService()?.removeRouting();
  }, [stopListening, removeMarkerA, removeMarkerB, getLayersService]);

  const calculateRoute = useCallback(async () => {
    if (!pointA || !pointB) {
      return;
    }

    setIsLoading(true);
    setRouteError(false);

    try {
      const response = await fetchDirections({
        origin: pointA,
        destination: pointB,
        profile: 'driving',
      });

      if (response?.routes?.[0]) {
        const route = response.routes[0];
        const nextRouteInfo: RouteInfo = {
          distance: route.distance,
          duration: route.duration,
        };
        setRouteInfo(nextRouteInfo);

        getLayersService()?.addRouting(response);

        onRouteComputed?.(nextRouteInfo);
      } else {
        // Directions returned nothing (already toasted by fetchDirections);
        // leave the map usable and surface "route unavailable".
        setRouteError(true);
      }
    } catch {
      // fetchDirections already toasts and normally returns undefined rather
      // than throwing; guard anyway so isLoading is always cleared (no spinner
      // stuck) and the failure is surfaced non-blockingly.
      setRouteError(true);
    } finally {
      setIsLoading(false);
    }
  }, [pointA, pointB, getLayersService, onRouteComputed]);

  // Auto-compute whenever both points are set. This single effect covers BOTH
  // the click-driven path (A then B) AND swap() — no separate swap trigger,
  // avoiding a double fetch.
  useEffect(() => {
    if (pointA && pointB) {
      calculateRoute();
    }
  }, [pointA, pointB, calculateRoute]);

  // Re-arm listening for the missing point after a clear (when not already
  // listening and at least one point is empty).
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-arm when points change
  useEffect(() => {
    if (armedRef.current && !isListening && (!pointA || !pointB)) {
      arm();
    }
  }, [pointA, pointB]);

  return useMemo(
    () => ({
      pointA,
      pointB,
      routeInfo,
      isLoading,
      isListening,
      routeError,
      arm,
      clearPointA,
      clearPointB,
      swap,
      reset,
    }),
    [
      pointA,
      pointB,
      routeInfo,
      isLoading,
      isListening,
      routeError,
      arm,
      clearPointA,
      clearPointB,
      swap,
      reset,
    ],
  );
}
